import { injectable } from 'inversify';
import { observable, action, computed } from 'mobx';
import { get as getCookie, remove as removeCookie } from 'js-cookie';
import { FullStory } from '@fullstory/browser';
import { whenFullStoryReady } from 'src/util/fullstory-initializer';

import { ClientDataApiService, LoginApiService } from 'src/api';
import { RouterStore } from './router.store';

export interface LoginData {
    loginType:
        | 'Portal'
        | 'PaymentLink'
        | 'EstimateLink'
        | 'StatementLink'
        | 'VisitAssistantLink'
        | 'UnsubscribeLink'
        | 'PhoneCaptureLink';
    email: string;
    defaultUrl?: string;
    customerName?: string;
    portalUserId: number;
}

@injectable()
export class ClientDataStore {
    @observable loginData!: LoginData;
    @observable isInitialized = false;
    @observable initError?: 'Generic' | 'EstimateDeleted';

    @computed get portalData() {
        return this.clientDataApi.getPortalDataObservable;
    }
    @computed get isLoggedIn() {
        return this.loginData.loginType === 'Portal';
    }
    @computed get isAnonymousPayments() {
        return this.loginData.loginType === 'PaymentLink';
    }
    @computed get isAnonymousEstimates() {
        return this.loginData.loginType === 'EstimateLink';
    }
    @computed get isAnonymousStatement() {
        return this.loginData.loginType === 'StatementLink';
    }
    @computed get isAnonymousVisitActionRequests() {
        return this.loginData.loginType === 'VisitAssistantLink';
    }
    @computed get isAnonymousUnsubscribe() {
        return this.loginData.loginType === 'UnsubscribeLink';
    }
    @computed get isAnonymousPhoneCapture() {
        return this.loginData.loginType === 'PhoneCaptureLink';
    }

    constructor(
        private clientDataApi: ClientDataApiService,
        private loginApi: LoginApiService,
        private router: RouterStore
    ) {
        this.updateLoginData();
    }

    init(): Promise<void> {
        return this.clientDataApi
            .getPortalData(true, {
                400: () => {
                    this.router.history.replace('/not-found?invalidTenant=1');
                    return null;
                },
                500: () => {
                    this.initError = 'Generic';
                    return null;
                },
            })
            .then(
                (resp) => {
                    this.isInitialized = true;
                },
                (err) => {
                    this.initError = 'EstimateDeleted';
                    if (err === 'This estimate no longer exists') {
                        this.initError = 'EstimateDeleted';
                    } else {
                        this.initError = 'Generic';
                    }
                }
            );
    }

    @action
    updateLoginData() {
        const cookie = getCookie('LoginData');
        this.loginData = cookie ? JSON.parse(atob(cookie)) : {};

        if (this.loginData?.portalUserId) {
            const userId = this.loginData.portalUserId.toString();
            const properties = {
                userName: this.loginData.email,
                email: this.loginData.email,
                tenantName: (window as any).PortalSettings?.tenantName || null,
                tenantId: (window as any).PortalSettings?.tenantId || null,
                service: 'CP1',
                customerPortalVersion: 'CP1'
            };
            whenFullStoryReady(() => FullStory('setIdentity', { uid: userId, properties }));
        }
    }

    removeLoginCookie() {
        removeCookie('LoginData');
        this.updateLoginData();
    }

    createTenantURL(payUrl: string): string {
        const portalHost = this.portalData?.brandData?.portalHost;
        if(portalHost) {
            const url = new URL(payUrl, location.origin);
            url.host = portalHost;
            return url.href;
        }
        else {
            // Try to guess subdomain using tenant name
            const tenantSubdomain = this.portalData?.tenant.replace(/_/g, "-");
            if(tenantSubdomain) {
                const url = new URL(payUrl, location.origin);
                url.hostname = url.hostname.replace(/^pay\b/, tenantSubdomain);
                return url.href;
            }
        }
        return payUrl;
    }

    @action
    logOut = () => {
        this.loginApi.logOut();
        this.removeLoginCookie();
        this.router.history.push('/');
    };
}
